import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import { LogIn, Camera } from 'lucide-react';
import { cn } from '../lib/utils';
import { useNavigate } from 'react-router-dom';
import { compressImage } from '../lib/mediaCompression';
import ImageCropper from '../components/ImageCropper';
import getCroppedImg from '../lib/cropImage';

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [birthday, setBirthday] = useState('');
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [isRegistering, setIsRegistering] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [showCropper, setShowCropper] = useState(false);
  const [originalImage, setOriginalImage] = useState<string | null>(null);
  const [isResettingPassword, setIsResettingPassword] = useState(false);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateBirthday = (date: string): boolean => {
    // Convert from MM/DD/YYYY to YYYY-MM-DD for Date object
    const [month, day, year] = date.split('/');
    const birthDate = new Date(`${year}-${month}-${day}`);
    const today = new Date();
    const minDate = new Date('1900-01-01');
    const age = today.getFullYear() - birthDate.getFullYear();
    
    // Check if date is valid
    if (isNaN(birthDate.getTime())) {
      return false;
    }
    
    // Check if date is within valid range
    if (birthDate > today || birthDate < minDate) {
      return false;
    }
    
    // Check if user is at least 13 years old
    if (age < 13) {
      return false;
    }
    
    return true;
  };

  const formatBirthday = (input: string): string => {
    // Remove any non-numeric characters
    const numbers = input.replace(/\D/g, '');
    
    // Format as MM/DD/YYYY
    if (numbers.length >= 8) {
      const month = numbers.slice(0, 2);
      const day = numbers.slice(2, 4);
      const year = numbers.slice(4, 8);
      return `${month}/${day}/${year}`;
    }
    
    return input;
  };

  const handleBirthdayChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    
    // Allow backspace and delete
    if (input.length < birthday.length) {
      setBirthday(input);
      return;
    }

    // Format the date as the user types
    if (input.length <= 10) {
      let formattedDate = input;
      
      // Add forward slash after month
      if (input.length === 2) {
        formattedDate += '/';
      }
      // Add forward slash after day
      else if (input.length === 5) {
        formattedDate += '/';
      }
      
      setBirthday(formattedDate);
    }
  };

  const handleForgotPassword = async () => {
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setMessage(null);

      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;

      setMessage('Password reset instructions have been sent to your email');
    } catch (err: any) {
      console.error('Error sending reset password email:', err);
      setError(err.message || 'Failed to send reset password email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate email format
    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError(null);
    setMessage(null);

    try {
      if (isRegistering) {
        if (!fullName.trim()) {
          throw new Error('Full name is required');
        }

        if (!validateBirthday(birthday)) {
          throw new Error('Please enter a valid birth date (You must be at least 13 years old)');
        }

        if (password.length < 6) {
          throw new Error('Password must be at least 6 characters long');
        }

        const { error: signUpError, data } = await supabase.auth.signUp({
          email: email.trim(),
          password,
          options: {
            data: {
              full_name: fullName.trim()
            }
          }
        });

        if (signUpError) {
          if (signUpError.message === 'User already registered') {
            throw new Error('This email is already registered. Please sign in instead.');
          }
          throw signUpError;
        }

        if (data.user) {
          let avatarUrl = null;

          if (avatarFile) {
            const fileExt = avatarFile.name.split('.').pop()?.toLowerCase() || '';
            const filePath = `${data.user.id}/${Date.now()}.${fileExt}`;

            const { error: uploadError } = await supabase.storage
              .from('avatars')
              .upload(filePath, avatarFile, {
                contentType: avatarFile.type,
                upsert: true
              });

            if (uploadError) throw uploadError;

            const { data: { publicUrl } } = supabase.storage
              .from('avatars')
              .getPublicUrl(filePath);

            avatarUrl = publicUrl;
          }

          let retries = 3;
          let profileError = null;

          while (retries > 0) {
            try {
              const { error } = await supabase
                .from('profiles')
                .insert([
                  {
                    id: data.user.id,
                    full_name: fullName.trim(),
                    avatar_url: avatarUrl,
                    birthday: birthday,
                  },
                ]);

              if (!error) {
                profileError = null;
                break;
              }

              profileError = error;
            } catch (err) {
              profileError = err;
            }

            retries--;
            if (retries > 0) {
              await new Promise(resolve => setTimeout(resolve, 1000));
            }
          }

          if (profileError) throw profileError;
        }
      } else {
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email: email.trim(),
          password,
        });

        if (signInError) {
          if (signInError.message.includes('Invalid login credentials')) {
            throw new Error('Invalid email or password. Please try again.');
          }
          throw signInError;
        }
      }
    } catch (err: any) {
      console.error('Auth error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      // Check file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setError('Please select a valid image file (JPEG, PNG, GIF, or WEBP)');
        return;
      }

      // Create preview URL for cropping
      if (avatarPreview) {
        URL.revokeObjectURL(avatarPreview);
      }
      const previewUrl = URL.createObjectURL(file);
      
      // Set original image for cropping
      setOriginalImage(previewUrl);
      setShowCropper(true);
      setError(null);
    } catch (err: any) {
      console.error('Error processing avatar:', err);
      setError(err.message || 'Failed to process image. Please try another file.');
    }
  };

  const handleCropComplete = async (croppedAreaPixels: any) => {
    if (!originalImage) return;
    
    try {
      // Get the cropped image as a blob
      const croppedImageBlob = await getCroppedImg(originalImage, croppedAreaPixels);
      
      // Convert blob to file
      const croppedFile = new File([croppedImageBlob], 'cropped-avatar.jpg', { 
        type: 'image/jpeg' 
      });
      
      // Process the file (compress if needed)
      let processedFile = croppedFile;
      if (croppedFile.size > 2 * 1024 * 1024) { // If larger than 2MB
        processedFile = await compressImage(croppedFile);
      }
      
      // Create preview URL
      const croppedPreviewUrl = URL.createObjectURL(processedFile);
      
      // Update state
      setAvatarFile(processedFile);
      setAvatarPreview(croppedPreviewUrl);
      setShowCropper(false);
      
      // Clean up original image URL
      if (originalImage) {
        URL.revokeObjectURL(originalImage);
        setOriginalImage(null);
      }
    } catch (err: any) {
      console.error('Error cropping image:', err);
      setError(err.message || 'Failed to crop image. Please try again.');
      setShowCropper(false);
    }
  };

  const handleCancelCrop = () => {
    setShowCropper(false);
    if (originalImage) {
      URL.revokeObjectURL(originalImage);
      setOriginalImage(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-xl shadow-lg">
        <div className="text-center">
          <LogIn className="mx-auto h-12 w-12 text-blue-500" />
          <h2 className="mt-6 text-3xl font-bold text-gray-900">
            {isRegistering ? 'Create an account' : 'Welcome Back Hurders!'}
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {isRegistering ? 'Sign up to start sharing' : 'Please sign in to your account'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="mt-8 space-y-6">
          <div className="space-y-4">
            {isRegistering && (
              <>
                <div className="flex justify-center">
                  <div className="relative">
                    <img
                      src={avatarPreview || `https://api.dicebear.com/7.x/initials/svg?seed=${fullName || 'Guest'}`}
                      alt="Avatar preview"
                      className="h-24 w-24 rounded-full object-cover bg-gray-100"
                    />
                    <label
                      htmlFor="avatar-upload"
                      className={cn(
                        "absolute bottom-0 right-0 p-2 rounded-full cursor-pointer",
                        "bg-blue-500 hover:bg-blue-600 transition-colors",
                        loading && "opacity-50 cursor-not-allowed"
                      )}
                    >
                      <Camera className="h-4 w-4 text-white" />
                      <input
                        type="file"
                        id="avatar-upload"
                        accept="image/*"
                        onChange={handleAvatarSelect}
                        disabled={loading}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>

                <div>
                  <label htmlFor="fullName" className="text-sm font-medium text-gray-700">
                    Full Name
                  </label>
                  <input
                    id="fullName"
                    type="text"
                    required
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label htmlFor="birthday" className="text-sm font-medium text-gray-700">
                    Birthday (MM/DD/YYYY)
                  </label>
                  <input
                    id="birthday"
                    type="text"
                    required
                    placeholder="MM/DD/YYYY"
                    pattern="\d{2}/\d{2}/\d{4}"
                    value={birthday}
                    onChange={handleBirthdayChange}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    You must be at least 13 years old to register
                  </p>
                </div>
              </>
            )}

            <div>
              <label htmlFor="email" className="text-sm font-medium text-gray-700">
                Email address
              </label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {!isResettingPassword && (
              <div>
                <label htmlFor="password" className="text-sm font-medium text-gray-700">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            )}
          </div>

          {error && (
            <div className="text-red-500 text-sm">{error}</div>
          )}

          {message && (
            <div className="text-green-500 text-sm">{message}</div>
          )}

          <div className="flex flex-col space-y-4">
            {isResettingPassword ? (
              <>
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  disabled={loading}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {loading ? 'Sending...' : 'Send Reset Instructions'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsResettingPassword(false);
                    setError(null);
                    setMessage(null);
                  }}
                  className="text-blue-600 hover:text-blue-500"
                >
                  Back to Sign In
                </button>
              </>
            ) : (
              <>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {loading
                    ? (isRegistering ? 'Creating account...' : 'Signing in...')
                    : (isRegistering ? 'Create account' : 'Sign in')}
                </button>

                <div className="flex flex-col space-y-2 text-sm">
                  <button
                    type="button"
                    onClick={() => {
                      setIsRegistering(!isRegistering);
                      setError(null);
                      setMessage(null);
                      setIsResettingPassword(false);
                      if (avatarPreview) {
                        URL.revokeObjectURL(avatarPreview);
                        setAvatarPreview(null);
                      }
                      setAvatarFile(null);
                      setBirthday('');
                    }}
                    className="text-blue-600 hover:text-blue-500"
                  >
                    {isRegistering
                      ? 'Already have an account? Sign in'
                      : "Don't have an account? Sign up"}
                  </button>

                  {!isRegistering && (
                    <button
                      type="button"
                      onClick={() => {
                        setIsResettingPassword(true);
                        setError(null);
                        setMessage(null);
                      }}
                      disabled={loading}
                      className="text-blue-600 hover:text-blue-500 disabled:opacity-50"
                    >
                      Forgot your password?
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </form>
      </div>

      {showCropper && originalImage && (
        <ImageCropper
          image={originalImage}
          onCropComplete={handleCropComplete}
          onCancel={handleCancelCrop}
          aspectRatio={1}
        />
      )}
    </div>
  );
}